import { api } from '../api/vykoClient';
import { userService } from '../services/userService';
import { NurseUser } from '../types';

export interface UserPreferencesConfig {
  preferredWardTypes: string[];
  preferredDepartments: string[];
  preferredRoomTypes: string[];
  preferredHandoverNurse: NurseUser | null;
}

export type PreferenceFilterType = 'department' | 'ward' | 'room';

export class UserSettingsModal {
  private mountPoint: HTMLElement;
  private onSaved: (prefs: UserPreferencesConfig) => void;
  private onClose: () => void;

  private activeSettingsTab: PreferenceFilterType = 'department';

  // User can select one option from each category: department, ward, room
  private selectedDepartment: string | null = null;
  private selectedWard: string | null = null;
  private selectedRoom: string | null = null;
  private preferredNurse: NurseUser | null = null;

  private availableWardTypes: string[] = [
    'GENERAL WARD',
    'ICU',
    'MICU',
    'SICU',
    'NICU',
    'PICU',
    'EMERGENCY WARD',
    'SPECIAL WARD',
    'SEMI SPECIAL',
    'POST OPERATIVE WARD',
    'DAY CARE',
    'DELUXE',
  ];

  private availableDepartments: string[] = [
    'GENERAL MEDICINE',
    'GENERAL SURGERY',
    'ORTHOPAEDICS',
    'OBSTETRICS & GYNAECOLOGY',
    'CARDIOLOGY',
    'CRITICAL CARE MEDICINE',
    'EMERGENCY MEDICINE',
    'NEUROLOGY',
    'NEURO SURGERY',
    'PAEDIATRICS',
    'NEO NATOLOGY',
    'PULMONOLOGY',
    'NEPHROLOGY',
    'MEDICAL ONCOLOGY',
    'SURGICAL ONCOLOGY',
    'ANAESTHESIA',
  ];

  private availableRoomTypes: string[] = [
    'MALE ORTHO WARD',
    'FEMALE ORTHO WARD',
    'OBG WARD',
    'FEMALE SURGICAL ONCOLOGY WARD',
    'MALE SURGICAL ONCOLOGY WARD',
    'MICU',
    'SICU',
    'NICU',
    'PICU',
    'ICU',
    'EMERGENCY WARD',
    'POST OPERATIVE WARD',
    'LABOUR ROOM',
  ];

  // Search filter query
  private searchQuery = '';
  private isSaving = false;

  private onKeyDown: ((e: KeyboardEvent) => void) | null = null;

  constructor(
    mountPoint: HTMLElement,
    knownWardTypes: string[],
    knownDepartments: string[],
    knownRoomTypes: string[],
    onSaved: (prefs: UserPreferencesConfig) => void,
    onClose?: () => void
  ) {
    this.mountPoint = mountPoint;
    this.onSaved = onSaved;
    this.onClose = onClose || (() => { });

    // Merge dynamically discovered hospital data
    this.availableWardTypes = Array.from(new Set([...this.availableWardTypes, ...knownWardTypes.filter(Boolean)])).sort();
    this.availableDepartments = Array.from(new Set([...this.availableDepartments, ...knownDepartments.filter(Boolean)])).sort();
    this.availableRoomTypes = Array.from(new Set([...this.availableRoomTypes, ...knownRoomTypes.filter(Boolean)])).sort();

    // Attach Escape key listener
    this.onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !this.isSaving) {
        this.destroy();
      }
    };
    window.addEventListener('keydown', this.onKeyDown);

    // 1. Immediately read cached preferences for instantaneous rendering
    const loginId = api.loginId || '';
    if (loginId) {
      const syncPrefs = userService.getUserPreferencesSync(loginId);
      this.initSelectionsFromPrefs(syncPrefs);
    }

    // Render immediately
    this.render();

    // 2. Fetch fresh from backend D1 asynchronously
    this.loadInitialPreferences();
  }

  private initSelectionsFromPrefs(prefs: Partial<UserPreferencesConfig> & { preferredWards?: string[] }) {
    if (prefs.preferredHandoverNurse) {
      this.preferredNurse = prefs.preferredHandoverNurse;
    }

    this.selectedDepartment = (prefs.preferredDepartments && prefs.preferredDepartments.length > 0)
      ? prefs.preferredDepartments[0]
      : null;

    this.selectedWard = (prefs.preferredWardTypes && prefs.preferredWardTypes.length > 0)
      ? prefs.preferredWardTypes[0]
      : null;

    this.selectedRoom = (prefs.preferredRoomTypes && prefs.preferredRoomTypes.length > 0)
      ? prefs.preferredRoomTypes[0]
      : null;

    // Fallback for legacy preferredWards field
    if (!this.selectedWard && !this.selectedRoom && prefs.preferredWards && prefs.preferredWards.length > 0) {
      const w = prefs.preferredWards[0];
      if (this.availableRoomTypes.includes(w)) {
        this.selectedRoom = w;
      } else {
        this.selectedWard = w;
      }
    }
  }

  private async loadInitialPreferences() {
    const loginId = api.loginId;
    if (loginId) {
      try {
        const prefs = await userService.getUserPreferences(loginId);
        this.initSelectionsFromPrefs(prefs);
        this.render();
      } catch (err) {
        console.warn('Could not refresh preferences from backend:', err);
      }
    }
  }

  private getActiveTabSelection(): string | null {
    if (this.activeSettingsTab === 'department') return this.selectedDepartment;
    if (this.activeSettingsTab === 'ward') return this.selectedWard;
    return this.selectedRoom;
  }

  private getActiveFilters(): { tag: string; label: string; type: PreferenceFilterType }[] {
    const activeFilters: { tag: string; label: string; type: PreferenceFilterType }[] = [];
    if (this.selectedDepartment) activeFilters.push({ tag: 'Dept', label: this.selectedDepartment, type: 'department' });
    if (this.selectedWard) activeFilters.push({ tag: 'Ward', label: this.selectedWard, type: 'ward' });
    if (this.selectedRoom) activeFilters.push({ tag: 'Room', label: this.selectedRoom, type: 'room' });
    return activeFilters;
  }

  public render() {
    let overlay = this.mountPoint.querySelector('#settings-modal-overlay');
    if (!overlay) {
      this.mountPoint.innerHTML = `
        <div class="settings-modal-overlay vyko-modal-overlay animate-fade-in" id="settings-modal-overlay">
          <div class="settings-clean-modal vyko-modal-card animate-scale-up" role="dialog" aria-modal="true" aria-labelledby="settings-dialog-title">
            
            <!-- Mobile Drag Handle -->
            <div class="sheet-drag-handle"></div>

            <!-- Modal Header -->
            <div class="settings-clean-header vyko-modal-header">
              <div class="settings-clean-header-info">
                <div class="settings-clean-title-row">
                  <h2 id="settings-dialog-title" class="settings-clean-title vyko-modal-title">Filter Preferences</h2>
                  <div id="settings-active-count-wrap"></div>
                </div>
                <p class="settings-clean-sub vyko-modal-sub">Personalize your station to filter the inpatient roster</p>
              </div>

              <button class="settings-clean-close vyko-modal-close" id="btn-close-settings" title="Close" type="button" aria-label="Close preferences">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"/>
                  <line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>

            <!-- Category Segmented Control (Departments / Ward Types / Room Types) -->
            <div class="settings-segmented-wrapper">
              <div class="settings-clean-segmented" role="tablist" aria-label="Filter category tabs">
                <button
                  class="settings-clean-seg-btn ${this.activeSettingsTab === 'department' ? 'active' : ''}"
                  id="tab-seg-depts"
                  type="button"
                  role="tab"
                  aria-selected="${this.activeSettingsTab === 'department'}"
                >
                  <span>Departments</span>
                </button>

                <button
                  class="settings-clean-seg-btn ${this.activeSettingsTab === 'ward' ? 'active' : ''}"
                  id="tab-seg-wards"
                  type="button"
                  role="tab"
                  aria-selected="${this.activeSettingsTab === 'ward'}"
                >
                  <span>Ward Types</span>
                </button>

                <button
                  class="settings-clean-seg-btn ${this.activeSettingsTab === 'room' ? 'active' : ''}"
                  id="tab-seg-rooms"
                  type="button"
                  role="tab"
                  aria-selected="${this.activeSettingsTab === 'room'}"
                >
                  <span>Room Types</span>
                </button>
              </div>
            </div>

            <!-- Dedicated Active Filter Strip Container -->
            <div id="settings-active-strip-container"></div>

            <!-- Search Filter Bar -->
            <div class="settings-clean-search-row">
              <div class="settings-clean-search-box">
                <svg class="search-box-icon" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
                  <circle cx="11" cy="11" r="8"/>
                  <line x1="21" y1="21" x2="16.65" y2="16.65"/>
                </svg>
                <input
                  type="text"
                  id="input-settings-search"
                  placeholder="Search departments..."
                  value=""
                  autocomplete="off"
                  spellcheck="false"
                />
                <button class="settings-clean-clear-btn" id="btn-clear-settings-search" type="button" title="Clear search" style="display: none;">✕</button>
              </div>
            </div>

            <!-- Options List (One Choice Per Category) -->
            <div class="settings-clean-body" role="listbox"></div>

            <!-- Clean Footer Summary & Actions -->
            <div class="settings-clean-footer vyko-modal-footer">
              <div class="settings-footer-left"></div>

              <div class="settings-footer-actions">
                <button type="button" class="btn btn-secondary btn-clean-cancel" id="btn-cancel-settings">
                  Cancel
                </button>
                <button
                  type="button"
                  class="btn btn-primary btn-clean-save"
                  id="btn-save-settings"
                >
                  <span>Apply Filters</span>
                </button>
              </div>
            </div>

          </div>
        </div>
      `;

      this.bindStaticEvents();
    }

    this.updateAll();
  }

  private bindStaticEvents() {
    const overlay = this.mountPoint.querySelector('#settings-modal-overlay');
    const btnClose = this.mountPoint.querySelector('#btn-close-settings');
    const btnCancel = this.mountPoint.querySelector('#btn-cancel-settings');
    const btnSave = this.mountPoint.querySelector('#btn-save-settings');

    // Close on overlay click
    overlay?.addEventListener('click', (e) => {
      if (e.target === overlay && !this.isSaving) {
        this.destroy();
      }
    });

    btnClose?.addEventListener('click', () => {
      if (!this.isSaving) this.destroy();
    });

    btnCancel?.addEventListener('click', () => {
      if (!this.isSaving) this.destroy();
    });

    // Segmented Navigation: Departments, Wards, Rooms
    this.mountPoint.querySelector('#tab-seg-depts')?.addEventListener('click', () => {
      if (this.activeSettingsTab !== 'department') {
        this.activeSettingsTab = 'department';
        this.searchQuery = '';
        this.updateAll(true);
      }
    });

    this.mountPoint.querySelector('#tab-seg-wards')?.addEventListener('click', () => {
      if (this.activeSettingsTab !== 'ward') {
        this.activeSettingsTab = 'ward';
        this.searchQuery = '';
        this.updateAll(true);
      }
    });

    this.mountPoint.querySelector('#tab-seg-rooms')?.addEventListener('click', () => {
      if (this.activeSettingsTab !== 'room') {
        this.activeSettingsTab = 'room';
        this.searchQuery = '';
        this.updateAll(true);
      }
    });

    // Search input - directly updates options list without re-rendering modal shell
    const searchInput = this.mountPoint.querySelector('#input-settings-search') as HTMLInputElement | null;
    searchInput?.addEventListener('input', (e) => {
      this.searchQuery = (e.target as HTMLInputElement).value;
      this.updateOptionsList();
      this.updateClearButton();
    });

    const btnClearSearch = this.mountPoint.querySelector('#btn-clear-settings-search');
    btnClearSearch?.addEventListener('click', () => {
      this.searchQuery = '';
      if (searchInput) searchInput.value = '';
      this.updateOptionsList();
      this.updateClearButton();
      searchInput?.focus();
    });

    // Save Preference
    btnSave?.addEventListener('click', async () => {
      if (this.isSaving) return;
      this.isSaving = true;
      this.updateFooter();

      const loginId = api.loginId;
      const config: UserPreferencesConfig = {
        preferredDepartments: this.selectedDepartment ? [this.selectedDepartment] : [],
        preferredWardTypes: this.selectedWard ? [this.selectedWard] : [],
        preferredRoomTypes: this.selectedRoom ? [this.selectedRoom] : [],
        preferredHandoverNurse: this.preferredNurse,
      };

      if (loginId) {
        await userService.saveUserPreferences(loginId, config);
      }

      this.onSaved(config);
      this.destroy();
    });
  }

  private updateAll(resetSearchInput = false) {
    this.updateHeaderAndPill();
    this.updateTabs();
    this.updateActiveStrip();
    this.updateSearchPlaceholder(resetSearchInput);
    this.updateOptionsList();
    this.updateClearButton();
    this.updateFooter();
  }

  private updateHeaderAndPill() {
    const wrap = this.mountPoint.querySelector('#settings-active-count-wrap') as HTMLElement | null;
    const activeFilters = this.getActiveFilters();
    if (wrap) {
      wrap.innerHTML = activeFilters.length > 0
        ? `<span class="settings-active-count-pill">${activeFilters.length} active</span>`
        : '';
    }
  }

  private updateTabs() {
    const tabDepts = this.mountPoint.querySelector('#tab-seg-depts');
    const tabWards = this.mountPoint.querySelector('#tab-seg-wards');
    const tabRooms = this.mountPoint.querySelector('#tab-seg-rooms');

    if (tabDepts) {
      tabDepts.className = `settings-clean-seg-btn ${this.activeSettingsTab === 'department' ? 'active' : ''}`;
      tabDepts.setAttribute('aria-selected', String(this.activeSettingsTab === 'department'));
      tabDepts.innerHTML = `<span>Departments</span>${this.selectedDepartment ? `<span class="seg-active-pill" title="Selected: ${this.selectedDepartment}">1</span>` : ''}`;
    }

    if (tabWards) {
      tabWards.className = `settings-clean-seg-btn ${this.activeSettingsTab === 'ward' ? 'active' : ''}`;
      tabWards.setAttribute('aria-selected', String(this.activeSettingsTab === 'ward'));
      tabWards.innerHTML = `<span>Ward Types</span>${this.selectedWard ? `<span class="seg-active-pill" title="Selected: ${this.selectedWard}">1</span>` : ''}`;
    }

    if (tabRooms) {
      tabRooms.className = `settings-clean-seg-btn ${this.activeSettingsTab === 'room' ? 'active' : ''}`;
      tabRooms.setAttribute('aria-selected', String(this.activeSettingsTab === 'room'));
      tabRooms.innerHTML = `<span>Room Types</span>${this.selectedRoom ? `<span class="seg-active-pill" title="Selected: ${this.selectedRoom}">1</span>` : ''}`;
    }
  }

  private updateActiveStrip() {
    const container = this.mountPoint.querySelector('#settings-active-strip-container') as HTMLElement | null;
    if (!container) return;

    const activeFilters = this.getActiveFilters();
    if (activeFilters.length === 0) {
      container.innerHTML = '';
      return;
    }

    container.innerHTML = `
      <div class="settings-active-strip">
        <div class="settings-active-strip-label">
          <span>Applied Filters</span>
          <button type="button" class="btn-strip-clear" id="btn-reset-filter" title="Clear all applied filters">Reset All</button>
        </div>
        <div class="settings-active-chips-wrap">
          ${activeFilters.map((f) => `
            <div class="settings-filter-chip">
              <span class="chip-tag">${f.tag}:</span>
              <span class="chip-val" title="${f.label}">${f.label}</span>
              <button type="button" class="chip-clear-btn" data-clear-type="${f.type}" title="Remove ${f.label}">
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>
          `).join('')}
        </div>
      </div>
    `;

    // Attach Reset All
    container.querySelector('#btn-reset-filter')?.addEventListener('click', () => {
      this.selectedDepartment = null;
      this.selectedWard = null;
      this.selectedRoom = null;
      this.updateAll();
    });

    // Attach individual clear buttons
    container.querySelectorAll('.chip-clear-btn[data-clear-type]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const type = btn.getAttribute('data-clear-type') as PreferenceFilterType;
        if (type === 'department') this.selectedDepartment = null;
        else if (type === 'ward') this.selectedWard = null;
        else if (type === 'room') this.selectedRoom = null;
        this.updateAll();
      });
    });
  }

  private updateSearchPlaceholder(resetValue = false) {
    const input = this.mountPoint.querySelector('#input-settings-search') as HTMLInputElement | null;
    if (!input) return;

    if (this.activeSettingsTab === 'department') {
      input.placeholder = 'Search departments...';
    } else if (this.activeSettingsTab === 'ward') {
      input.placeholder = 'Search ward types...';
    } else {
      input.placeholder = 'Search room types...';
    }

    if (resetValue) {
      input.value = '';
    }
  }

  private updateClearButton() {
    const btn = this.mountPoint.querySelector('#btn-clear-settings-search') as HTMLElement | null;
    if (btn) {
      btn.style.display = this.searchQuery.trim() ? 'flex' : 'none';
    }
  }

  private updateOptionsList() {
    const body = this.mountPoint.querySelector('.settings-clean-body') as HTMLElement | null;
    if (!body) return;

    let currentOptions: string[] = [];
    let currentCategoryLabel = '';

    if (this.activeSettingsTab === 'department') {
      currentOptions = this.availableDepartments;
      currentCategoryLabel = 'Department';
    } else if (this.activeSettingsTab === 'ward') {
      currentOptions = this.availableWardTypes;
      currentCategoryLabel = 'Ward Type';
    } else {
      currentOptions = this.availableRoomTypes;
      currentCategoryLabel = 'Room Type';
    }

    const q = this.searchQuery.trim().toLowerCase();
    const filteredOptions = q
      ? currentOptions.filter((opt) => opt.toLowerCase().includes(q))
      : currentOptions;

    const currentSelectedValue = this.getActiveTabSelection();
    const isCurrentCategoryNone = !currentSelectedValue;

    let html = '';

    // Default "All" option when not searching
    if (!q) {
      html += `
        <div
          class="settings-clean-row ${isCurrentCategoryNone ? 'is-selected' : ''}"
          data-action="clear-category"
          role="option"
          aria-selected="${isCurrentCategoryNone}"
          tabindex="0"
        >
          <div class="settings-row-main">
            <span class="settings-row-title">All ${currentCategoryLabel === 'Department' ? 'Departments' : currentCategoryLabel === 'Ward Type' ? 'Ward Types' : 'Room Types'}</span>
            <span class="settings-row-badge-pill">Default</span>
          </div>
          <div class="settings-row-indicator">
            <div class="settings-check-circle ${isCurrentCategoryNone ? 'checked' : ''}">
              ${isCurrentCategoryNone ? `
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
              ` : ''}
            </div>
          </div>
        </div>
      `;
    }

    if (filteredOptions.length === 0) {
      html += `
        <div class="settings-empty-state">
          <div class="settings-empty-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="11" cy="11" r="8"/>
              <line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
          </div>
          <p class="settings-empty-title">No matching ${currentCategoryLabel.toLowerCase()}s</p>
          <p class="settings-empty-sub">Check spelling or try a different keyword</p>
        </div>
      `;
    } else {
      html += filteredOptions.map((opt) => {
        const isSelected = currentSelectedValue === opt;
        return `
          <div
            class="settings-clean-row ${isSelected ? 'is-selected' : ''}"
            data-type="${this.activeSettingsTab}"
            data-value="${opt}"
            role="option"
            aria-selected="${isSelected}"
            tabindex="0"
          >
            <div class="settings-row-main">
              <span class="settings-row-title">${opt}</span>
            </div>
            <div class="settings-row-indicator">
              <div class="settings-check-circle ${isSelected ? 'checked' : ''}">
                ${isSelected ? `
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                ` : ''}
              </div>
            </div>
          </div>
        `;
      }).join('');
    }

    body.innerHTML = html;

    // Clear Active Category handler
    body.querySelector('[data-action="clear-category"]')?.addEventListener('click', () => {
      if (this.activeSettingsTab === 'department') {
        this.selectedDepartment = null;
      } else if (this.activeSettingsTab === 'ward') {
        this.selectedWard = null;
      } else if (this.activeSettingsTab === 'room') {
        this.selectedRoom = null;
      }
      this.updateAll();
    });

    // Option row selection handlers
    body.querySelectorAll('.settings-clean-row[data-type]').forEach((row) => {
      row.addEventListener('click', () => {
        const type = row.getAttribute('data-type') as PreferenceFilterType;
        const value = row.getAttribute('data-value') || '';
        if (!type || !value) return;

        if (type === 'department') {
          this.selectedDepartment = this.selectedDepartment === value ? null : value;
        } else if (type === 'ward') {
          this.selectedWard = this.selectedWard === value ? null : value;
        } else if (type === 'room') {
          this.selectedRoom = this.selectedRoom === value ? null : value;
        }
        this.updateAll();
      });
    });
  }

  private updateFooter() {
    const footerLeft = this.mountPoint.querySelector('.settings-footer-left') as HTMLElement | null;
    const activeFilters = this.getActiveFilters();
    if (footerLeft) {
      if (activeFilters.length > 0) {
        footerLeft.innerHTML = `
          <span class="settings-footer-status">
            <span class="footer-status-dot"></span>
            <span class="footer-status-text"><strong>${activeFilters.length}</strong> ${activeFilters.length === 1 ? 'filter' : 'filters'} active</span>
          </span>
        `;
      } else {
        footerLeft.innerHTML = `
          <span class="settings-footer-status muted">
            <span class="footer-status-text">Showing all inpatients</span>
          </span>
        `;
      }
    }

    const saveBtn = this.mountPoint.querySelector('#btn-save-settings') as HTMLButtonElement | null;
    if (saveBtn) {
      saveBtn.disabled = this.isSaving;
      saveBtn.innerHTML = this.isSaving
        ? `<div class="spinner" style="width: 14px; height: 14px; border-width: 2px;"></div><span>Saving...</span>`
        : `<span>Apply Filters</span>`;
    }
  }

  public destroy() {
    if (this.onKeyDown) {
      window.removeEventListener('keydown', this.onKeyDown);
      this.onKeyDown = null;
    }
    this.mountPoint.innerHTML = '';
    this.onClose();
  }
}
